import { ColumnDef } from "@tanstack/react-table";
import { Option } from "./model";
import { Dialog, DialogTrigger } from "@/src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";
import { EllipsisVerticalIcon } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { Link } from "react-router-dom";

// Human date: "01 Jan 2026". Returns null for missing/placeholder (epoch-zero /
// year-1) dates so callers render an em-dash instead of "0001-01-01".
function fmtDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime()) || d.getUTCFullYear() < 2000) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}

function yachtLabel(o: Option): string {
  const name = o.yacht?.name?.trim();
  const model = o.yacht?.modelName?.trim();
  if (name && model && model.toLowerCase() !== name.toLowerCase()) return `${name} · ${model}`;
  return name || model || "—";
}

export const optionsColumns = (onRowClick: (r: Option) => void): ColumnDef<Option>[] => {
  return [
    {
      header: "Provider",
      accessorKey: "provider",
      cell: ({ getValue }) => (
        <span className="text-xs font-medium bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
          {String(getValue() ?? "")}
        </span>
      ),
    },
    {
      header: "Yacht",
      cell: ({ row }) => <span className="font-medium">{yachtLabel(row.original)}</span>,
    },
    {
      header: "Operator",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-slate-600">{row.original.yacht?.operator?.name ?? "—"}</span>
      ),
    },
    {
      header: "Charter dates",
      cell: ({ row }) => {
        const from = fmtDate(row.original.startDate);
        const to = fmtDate(row.original.endDate);
        return <span className="whitespace-nowrap">{from && to ? `${from} – ${to}` : from || to || "—"}</span>;
      },
    },
    {
      header: "Type",
      cell: ({ row }) => {
        const isOption = row.original.isOption;
        return (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              isOption ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {isOption ? "Option" : "Booking"}
          </span>
        );
      },
    },
    {
      header: "Expires",
      cell: ({ row }) => <span className="whitespace-nowrap">{fmtDate(row.original.expirationDate) ?? "—"}</span>,
    },
    {
      header: "Created",
      cell: ({ row }) => <span className="whitespace-nowrap">{fmtDate(row.original.createdAt) ?? "—"}</span>,
    },
    {
      header: "Offer",
      cell: ({ row }) => {
        const offerId = row.original.offer?.offer?.id;
        return offerId ? (
          <Link
            to={`/offers/edit/${offerId}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          >
            View offer →
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
    {
      id: "actions",
      size: 32,
      cell({ row }) {
        return (
          <Dialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="p-1 h-fit">
                  <EllipsisVerticalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DialogTrigger onClick={() => onRowClick(row.original)}>
                  <DropdownMenuItem>See details</DropdownMenuItem>
                </DialogTrigger>
              </DropdownMenuContent>
            </DropdownMenu>
          </Dialog>
        );
      },
    },
  ];
};
